import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

export default function ProfilimPage() {
  const { ogretmenRecord, profile } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({
    telefon: ogretmenRecord?.telefon || profile?.telefon || '',
    musaitlik_notu: ogretmenRecord?.musaitlik_notu || '',
    aciklama: ogretmenRecord?.aciklama || '',
  });

  if (!ogretmenRecord || !profile) {
    return (
      <div className="p-6 flex items-center justify-center py-32">
        <i className="ri-loader-4-line text-3xl text-slate-300 animate-spin" />
      </div>
    );
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    await supabase.from('egtpanel_ogretmenler').update({
      telefon: form.telefon || null,
      musaitlik_notu: form.musaitlik_notu || null,
      aciklama: form.aciklama || null,
      guncelleme_tarihi: new Date().toISOString(),
    }).eq('id', ogretmenRecord.id);
    setSaving(false);
    setEditing(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div className="p-6 space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Profilim</h1>
        <p className="text-slate-500 text-sm mt-1">Profil bilgilerinizi görüntüleyin ve düzenleyin</p>
      </div>

      {saved && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
          <i className="ri-checkbox-circle-line text-green-600 text-xl" />
          <p className="text-green-800 font-medium text-sm">Profil güncellendi!</p>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-100 p-6">
        <div className="flex items-center gap-5 mb-6">
          <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-purple-600 text-white text-3xl font-bold">
            {profile.ad_soyad?.charAt(0) || '?'}
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800">{profile.ad_soyad}</h2>
            <p className="text-slate-500 text-sm">{ogretmenRecord.branslar?.join(', ') || '—'}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${ogretmenRecord.aktif ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                {ogretmenRecord.aktif ? 'Aktif' : 'Pasif'}
              </span>
              {ogretmenRecord.online_ders && <span className="bg-cyan-50 text-cyan-700 px-2 py-0.5 rounded-full text-xs">Online</span>}
              {ogretmenRecord.yuz_yuze_ders && <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full text-xs">Yüz Yüze</span>}
            </div>
          </div>
          <button onClick={() => setEditing(!editing)} className="ml-auto flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors cursor-pointer">
            <i className={editing ? 'ri-close-line' : 'ri-edit-line'} />
            {editing ? 'İptal' : 'Düzenle'}
          </button>
        </div>

        {editing ? (
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Telefon</label>
              <input value={form.telefon} onChange={e => setForm(p => ({...p, telefon: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Müsaitlik</label>
              <input value={form.musaitlik_notu} onChange={e => setForm(p => ({...p, musaitlik_notu: e.target.value}))} placeholder="örn: Hafta içi 14:00-20:00" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Kendiniz Hakkında</label>
              <textarea value={form.aciklama} onChange={e => setForm(p => ({...p, aciklama: e.target.value}))} rows={3} maxLength={500} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 resize-none" />
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setEditing(false)} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer">İptal</button>
              <button type="submit" disabled={saving} className="flex-1 py-2.5 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 cursor-pointer">
                {saving ? <i className="ri-loader-4-line animate-spin" /> : 'Kaydet'}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-2">
            {[
              { label: 'E-posta', val: profile.email || '—', icon: 'ri-mail-line' },
              { label: 'Telefon', val: ogretmenRecord.telefon || '—', icon: 'ri-phone-line' },
              { label: 'Müsaitlik', val: ogretmenRecord.musaitlik_notu || '—', icon: 'ri-time-line' },
              { label: 'Ders Ücreti', val: ogretmenRecord.varsayilan_ders_ucreti ? `₺${ogretmenRecord.varsayilan_ders_ucreti} / ders` : '—', icon: 'ri-money-dollar-circle-line' },
              { label: 'Hak Ediş Oranı', val: `%${ogretmenRecord.hakedis_orani || 50}`, icon: 'ri-percent-line' },
            ].map(r => (
              <div key={r.label} className="flex items-center gap-3 py-2.5 border-b border-slate-50">
                <i className={`${r.icon} text-slate-400 text-base w-5`} />
                <span className="text-xs text-slate-400 w-28 shrink-0">{r.label}</span>
                <span className="text-sm text-slate-700 font-medium">{r.val}</span>
              </div>
            ))}
            {ogretmenRecord.aciklama && (
              <div className="bg-amber-50 rounded-lg p-3 text-sm text-amber-800 mt-2">
                <i className="ri-sticky-note-line mr-1" /> {ogretmenRecord.aciklama}
              </div>
            )}
          </div>
        )}
      </div>

      {ogretmenRecord.branslar && ogretmenRecord.branslar.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-100 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Branşlarım</h3>
          <div className="flex flex-wrap gap-2">
            {ogretmenRecord.branslar.map((b: string) => (
              <span key={b} className="bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-full text-sm font-medium">{b}</span>
            ))}
          </div>
        </div>
      )}

      {ogretmenRecord.sinif_seviyeleri && ogretmenRecord.sinif_seviyeleri.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-100 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Sınıf Seviyeleri</h3>
          <div className="flex flex-wrap gap-2">
            {ogretmenRecord.sinif_seviyeleri.map((g: string) => (
              <span key={g} className="bg-slate-100 text-slate-600 px-3 py-1.5 rounded-full text-sm font-medium">{g}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}